
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SavedAddress } from "@/types/address";
import { toast } from "@/components/ui/use-toast";

export const useAddressBook = () => {
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadSavedAddresses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Only select addresses that have been explicitly saved by the user
      const { data, error } = await supabase
        .from('address_book')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_saved', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSavedAddresses(data || []);
      console.log('Loaded saved addresses:', data?.length || 0);
    } catch (error) {
      console.error('Error loading addresses:', error);
      setError(error instanceof Error ? error : new Error('Failed to load addresses'));
      toast({
        title: "Error",
        description: "Failed to load saved addresses",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveAddress = async (address: Omit<SavedAddress, 'id'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to save addresses",
          variant: "destructive",
        });
        return;
      }

      // Set is_saved to true for explicitly saved addresses
      const { error } = await supabase
        .from('address_book')
        .insert([{ ...address, user_id: user.id, is_saved: true }]);

      if (error) throw error;

      toast({
        title: "Address saved",
        description: "The address has been saved to your address book",
      });
      
      await loadSavedAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      toast({
        title: "Error",
        description: "Failed to save address. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Also update the function in AddressActions to do the same
  const updateExistingAddressAsSaved = async (addressId: string, label: string) => {
    try {
      const { error } = await supabase
        .from('address_book')
        .update({ label, is_saved: true })
        .eq('id', addressId);

      if (error) throw error;
      
      toast({
        title: "Address saved",
        description: "The address has been saved to your address book",
      });
      
      await loadSavedAddresses();
      return true;
    } catch (error) {
      console.error('Error updating address:', error);
      toast({
        title: "Error",
        description: "Failed to save address. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    loadSavedAddresses();
  }, []);

  return {
    savedAddresses,
    isLoading,
    error,
    loadSavedAddresses,
    saveAddress,
    updateExistingAddressAsSaved,
  };
};
